import { useMemo, useRef } from "react"
import { GraphQLSubscriptionConfig } from "relay-runtime"
import { useConditionalSubscription } from "@/modules/patients/hooks/useConditionalSubscription"
import { DuplicatePatient } from "@/modules/patients/manage-duplicates/manage-duplicates-patient-item"
import type { useChDuplicatePatientChangedSubscription as ChSubscriptionType } from "@/modules/patients/manage-duplicates/services/ch/__generated__/useChDuplicatePatientChangedSubscription.graphql"
import { chDuplicatePatientChangedSubscription } from "@/modules/patients/manage-duplicates/services/ch/useChDuplicatePatientChangedSubscription"
import type { useDeDuplicatePatientChangedSubscription as DeSubscriptionType } from "@/modules/patients/manage-duplicates/services/de/__generated__/useDeDuplicatePatientChangedSubscription.graphql"
import { deDuplicatePatientChangedSubscription } from "@/modules/patients/manage-duplicates/services/de/useDeDuplicatePatientChangedSubscription"
import { Country } from "@/types/__generated__/union-types"

type SubscriptionType = ChSubscriptionType | DeSubscriptionType

const useDuplicatePatientChangedSubscription = (
  country: Country,
  patientIds: string[],
  onPatientChanged: (patient: DuplicatePatient) => void,
) => {
  const patientIdsRef = useRef(patientIds)
  patientIdsRef.current = patientIds

  const onPatientChangedRef = useRef(onPatientChanged)
  onPatientChangedRef.current = onPatientChanged

  const subscription = country === "CH" ? chDuplicatePatientChangedSubscription : deDuplicatePatientChangedSubscription

  const config: GraphQLSubscriptionConfig<SubscriptionType> = useMemo(
    () => ({
      subscription,
      variables: {},
      onNext: (response) => {
        if (!response) return
        const patient = "ch_patientChanged" in response ? response.ch_patientChanged : response.de_patientChanged
        if (patientIdsRef.current.includes(patient.id)) {
          onPatientChangedRef.current(patient)
        }
      },
    }),
    [subscription],
  )

  useConditionalSubscription<SubscriptionType>(config, { skip: patientIds.length === 0 })
}

export { useDuplicatePatientChangedSubscription }
